import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BlankMediaTypeImporterSummaryViewModel,
  SubmitBlankMediaImporterApplication,
} from "../../../../../../generated/data-contracts";
import { IBlankMediaTypeSectionBase } from "../../../../../../models";
import {
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import { AvisContext } from "../../../../../shared/context";
import { ApplicationSectionBase, ISectionSharedProps } from "../../forms";
import { fieldCollectionEquipmentAndCNCode } from "./fields/SharedFieldCollection";
import { AvisConstants } from "../../../../../../data/avisConstants";
import {
  parseFloatOrDefault,
  parseIntOrDefault,
} from "../../../../../../utils";

export function BlankMediaApplicationSummaries(
  props: Readonly<ISectionSharedProps<SubmitBlankMediaImporterApplication>>,
) {
  const { bag, bagField, equipments, loading } = props;
  const { res } = useContext(AvisContext);
  const [existingSummaryIds, setExistingSummaryIds] = useState<object>({});
  const [lastBagValues, setLastBagValues] =
    useState<SubmitBlankMediaImporterApplication>(bag.values);
  const sharedFields = useMemo(
    () =>
      fieldCollectionEquipmentAndCNCode<BlankMediaTypeImporterSummaryViewModel>(
        equipments,
        res,
        true,
      ),
    [equipments, res],
  );

  const getEquipmentCnCodeKey = (sourceO: any): string => {
    if (!sourceO) return "";

    return `e#${sourceO.equipmentId}-${sourceO.cnCode}`;
  };

  useEffect(() => {
    const existingSummaryIdsMap: any = {};

    (bag.values.summaries || []).forEach((summaryItemInit) => {
      existingSummaryIdsMap[getEquipmentCnCodeKey(summaryItemInit)] =
        summaryItemInit.blankMediaTypeImporterSummaryId;
    });
    setExistingSummaryIds(existingSummaryIdsMap);
  }, [bag.values.summaries]);

  const addSummaryRange = useMemo(
    () => (keyCollection: any, collection: IBlankMediaTypeSectionBase[]) => {
      const foundKeys = collection
        .filter((item) => item.rowState !== "Delete" && item.equipmentId)
        .map((item) => {
          return {
            keyE: getEquipmentCnCodeKey(item),
            item: {
              equipmentId: item.equipmentId,
              cnCode: item.cnCode ?? undefined,
            },
          };
        });

      foundKeys.forEach((fKey) => {
        //Appends to unique key collection
        if (!keyCollection[fKey.keyE]) keyCollection[fKey.keyE] = fKey.item;
      });
    },
    [],
  );

  const recalculateSummaries = useCallback(
    (objectO: any) => {
      if (JSON.stringify(objectO) === JSON.stringify(lastBagValues)) return;
      const equipmentCNCodeDictionary = {};

      //Collect all possible equipment & cncode value combinations
      addSummaryRange(
        equipmentCNCodeDictionary,
        bag.values.importedFromCountries ?? [],
      );
      addSummaryRange(
        equipmentCNCodeDictionary,
        bag.values.exportedToCountries ?? [],
      );
      addSummaryRange(equipmentCNCodeDictionary, bag.values.sales ?? []);
      addSummaryRange(
        equipmentCNCodeDictionary,
        bag.values.expropriations ?? [],
      );
      addSummaryRange(
        equipmentCNCodeDictionary,
        bag.values.manufEquipments ?? [],
      );

      const arrayOfSummaries: IRowState<BlankMediaTypeImporterSummaryViewModel>[] =
        [];

      Object.entries<IRowState<BlankMediaTypeImporterSummaryViewModel>>(
        equipmentCNCodeDictionary,
      ).forEach(([, value]) => {
        arrayOfSummaries.push({
          ...value,
          rowState: "View",
        } as BlankMediaTypeImporterSummaryViewModel);
      });

      ///////////
      //Calculating and setting up values for summary row based on other sections
      //Calculating values foreach found unique equipment & cnCode pair
      ///////////
      arrayOfSummaries.forEach((summaryItem) => {
        const keyE = getEquipmentCnCodeKey(summaryItem);
        const foundKeyEId = (existingSummaryIds as any)[keyE];
        if (foundKeyEId)
          summaryItem.blankMediaTypeImporterSummaryId = parseInt(foundKeyEId);

        const filterSameEquipmentAndCnCode = (
          item: IBlankMediaTypeSectionBase,
        ): boolean => {
          return (
            item.equipmentId === summaryItem.equipmentId &&
            item.cnCode === summaryItem.cnCode &&
            item.rowState !== "Delete"
          );
        };
        //imported = Kolonnas “Skaits” kopsumma - sk. sarakstu “Latvijā ievestie TDN” un “Izgatavotie TDN”
        const importedCountries = bag.values.importedFromCountries
          ?.filter(filterSameEquipmentAndCnCode)
          .reduce((sum, current) => sum + parseIntOrDefault(current.count), 0);
        const importedManufEquipments = bag.values.manufEquipments
          ?.filter(filterSameEquipmentAndCnCode)
          .reduce((sum, current) => sum + parseIntOrDefault(current.count), 0);

        summaryItem.imported =
          (importedCountries ?? 0) + (importedManufEquipments ?? 0);

        //exported = Kolonnas “Skaits” kopsumma - sk. tabulu “No Latvijas izvestie TDN”
        const exportedCountries = bag.values.exportedToCountries
          ?.filter(filterSameEquipmentAndCnCode)
          .reduce((sum, current) => sum + parseIntOrDefault(current.count), 0);

        summaryItem.exported = exportedCountries ?? 0;

        //sold = Kolonnas “Skaits” kopsumma - sk. tabulu “Prof. lietotājiem un Izgatavotājiem pārdotie TDN”
        const salesSum = bag.values.sales
          ?.filter(filterSameEquipmentAndCnCode)
          .reduce((sum, current) => sum + parseIntOrDefault(current.count), 0);

        summaryItem.sold = salesSum ?? 0;

        //Atsavināšanas summa bez PVN, Eur = Kopsumma no tabulas “Atsavināšanas dati” kolonnas “Summa”
        const expropriationAmountSum = bag.values.expropriations
          ?.filter(filterSameEquipmentAndCnCode)
          .reduce(
            (sum, current) =>
              sum + +parseFloatOrDefault(current.amount).toFixed(2),
            0,
          );
        summaryItem.expropriationAmount = expropriationAmountSum ?? 0;

        //balance  = Aprēķins: pēc šīs tabulas kolonnām: “Atlikums” =“Ievests un Izgatavots” – - “Izvests” – “Pārdots”
        summaryItem.balance =
          summaryItem.imported - summaryItem.exported - summaryItem.sold;

        //rateType - from equipment
        //rate - from equipment
        //paymentAmount =
        // 1)Ja  likme ir procenti no summas ([RateType] =1), “Aprēķins, EUR” = “Atsavināšanas summa”  x “Likme” x 0.001
        // 2)Ja  likme ir par gabalu ([RateType] =2), “Aprēķins, EUR” = “Atlikums” x “Likme”
        const equipmentObject = equipments?.filter(
          (item) => item.id === summaryItem.equipmentId,
        )[0];
        if (!equipmentObject) {
          console.error(
            Error(
              `Equipment item not found by id: ${summaryItem.equipmentId}!`,
            ),
          );
          return;
        }
        summaryItem.rateType = "NoData";
        summaryItem.rate = 0;
        if (equipmentObject.amount && equipmentObject.amount > 0) {
          summaryItem.rateType = "AmountPerItem";
          summaryItem.rate = equipmentObject.amount;
        } else if (
          equipmentObject.percentage &&
          equipmentObject.percentage > 0
        ) {
          summaryItem.rateType = "Percentage";
          summaryItem.rate = equipmentObject.percentage;
        }

        switch (summaryItem.rateType) {
          case "AmountPerItem": {
            summaryItem.paymentAmount = +(
              (summaryItem.balance ?? 0) * summaryItem.rate
            ).toFixed(2);
            break;
          }
          case "Percentage": {
            summaryItem.paymentAmount = +(
              (summaryItem.expropriationAmount ?? 0) *
              summaryItem.rate *
              0.01
            ).toFixed(2);
            break;
          }
          default: {
            throw Error(`Can not calculate paymentAmount for row: ${keyE}`);
          }
        }
      });

      setLastBagValues(objectO);
      bag.setFieldValue("summaries", arrayOfSummaries);
    },
    [addSummaryRange, bag, equipments, existingSummaryIds, lastBagValues],
  );

  useEffect(() => {
    // disable no-used-vars for destructured prop summaries (removing summary array from compare object)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { summaries, ...restOfObject } = bag.values;

    //to reduce redrawing times & performance - recalculate after timeout
    const timeout = setTimeout(() => {
      recalculateSummaries(restOfObject);
    }, 200);

    return () => {
      // clears timeout before running the new effect
      clearTimeout(timeout);
    };
  }, [bag, recalculateSummaries]);

  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<BlankMediaTypeImporterSummaryViewModel>
  >[] = [
    ...sharedFields,
    {
      name: "imported",
      title: res("blankMedia_Application_label_Imported"),
      width: 75,
    },
    {
      name: "exported",
      title: res("blankMedia_Application_label_Exported"),
      width: 75,
    },
    {
      name: "sold",
      title: res("blankMedia_Application_label_Sold"),
      width: 75,
    },
    {
      name: "balance",
      title: res("blankMedia_Application_label_Balance"),
      width: 75,
    },
    {
      name: "expropriationAmount",
      title: res("blankMedia_Application_label_ExpropriationAmount"),
      width: 100,
      dataType: "Double",
    },
    {
      name: "rate",
      title: res("blankMedia_Application_label_Rate"),
      width: 120,
      transformViewValue: (fieldValue, rowValue) => {
        const valueNumber = parseFloatOrDefault(fieldValue);
        const valueToSet = res(
          `value_format_${rowValue.rateType}`,
          valueNumber.toFixed(2),
        ).replace(/[.,]/g, AvisConstants.default_delimiter);
        return valueToSet;
      },
    },
    {
      name: "paymentAmount",
      title: res("blankMedia_Application_label_PaymentAmount"),
      width: 100,
      dataType: "Double",
    },
  ];

  return (
    <ApplicationSectionBase
      title={res("blankMedia_Application_section_I_Summary")}
      textToDisplay="portal_TDN_ImporterReport_Summary"
      bag={bag}
      bagField={bagField}
      initValue={{} as IRowState<BlankMediaTypeImporterSummaryViewModel>}
      fieldCollection={fieldCollection}
      loading={loading}
      disabled
      collapsable={false}
    />
  );
}
